import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../../ui";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { CompleteMarkdown } from "../internal/CompleteMarkdown";
import { type LinearDocumentPayload, parseLinearDocumentPayload } from "./linearDocumentSchema";
import { LinearIcon } from "./linearIcon";
import { safeLinearUrl } from "./linearRenderUtils";

interface LinearDocumentRendererProps {
  block: StreamingBlock;
}

export const LinearDocumentRenderer = (props: LinearDocumentRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LinearDocumentPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLinearDocumentPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("linear_document_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
            <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
              <LinearIcon class="size-4 shrink-0 text-primary-text" />
              <Show
                when={safeLinearUrl(data().url)}
                fallback={
                  <span class="truncate text-sm font-medium text-primary-text">
                    {data().title || rt.t("linear_document_card.title")}
                  </span>
                }
              >
                {(href) => (
                  <a
                    href={href()}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex min-w-0 items-center gap-1 text-sm font-medium text-brand hover:underline"
                  >
                    <span class="truncate">{data().title || rt.t("linear_document_card.title")}</span>
                    <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                  </a>
                )}
              </Show>
              <Show when={data().project}>
                {(project) => <span class="ml-auto shrink-0 truncate text-xs text-muted-text">{project()}</span>}
              </Show>
            </div>

            <Show
              when={data().content.trim()}
              fallback={<div class="p-3 text-sm text-muted-text">{rt.t("linear_document_card.empty")}</div>}
            >
              {(content) => (
                <div class="px-3 py-2">
                  <ScrollableFadeContainer maxHeight="200px">
                    <CompleteMarkdown markdown={content()} />
                  </ScrollableFadeContainer>
                </div>
              )}
            </Show>

            <Show when={formatIsoTimestamp(data().updatedAt)}>
              {(updated) => (
                <div class="border-t border-primary-border px-3 py-2 text-xs text-muted-text">
                  {rt.t("linear_document_card.updated", { date: updated() })}
                </div>
              )}
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};
